import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, Calendar, User, Share2, Facebook, Linkedin } from "lucide-react";
import SEO from "@/components/SEO";
import { CTAButton, SectionLabel } from "@/components/common/Bits";
import { Reveal } from "@/components/motion/Reveal";
import { Spinner } from "@/components/common/States";
import { WhatsAppIcon } from "@/components/ui/FloatingWhatsApp";
import { sanitizeHtml } from "@/lib/sanitizeHtml";
import { getBlogPostBySlug, getBlogPosts } from "@/services/api";
import { waLink, WA_MESSAGES } from "@/data/company";

function ArticleBody({ html }) {
    // Sanitised again at render time: content written before a rules change, or
    // restored from a backup, is held to the same allowlist as new content.
    const safe = useMemo(() => sanitizeHtml(html), [html]);
    // eslint-disable-next-line react/no-danger
    return <div className="article-prose mt-8" dangerouslySetInnerHTML={{ __html: safe }} />;
}

export default function BlogPost() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let active = true;
        setLoading(true);
        Promise.all([getBlogPostBySlug(slug), getBlogPosts()]).then(([p, all]) => {
            if (!active) return;
            setPost(p);
            setRelated(all.filter((x) => x.slug !== slug).slice(0, 3));
            setLoading(false);
        });
        return () => {
            active = false;
        };
    }, [slug]);

    if (loading) return <div className="flex min-h-[60vh] items-center justify-center pt-24"><Spinner /></div>;

    if (!post) {
        return (
            <div className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 pt-24 text-center">
                <h1 className="font-display text-3xl font-semibold text-brand-blue">Article not found</h1>
                <CTAButton onClick={() => navigate("/blog")}>Back to Blog</CTAButton>
            </div>
        );
    }

    const fmt = (d) => new Date(d).toLocaleDateString("en-SG", { day: "numeric", month: "long", year: "numeric" });
    const shareUrl = window.location.href;

    return (
        <>
            <SEO title={post.title} path={`/blog/${slug}`} description={post.excerpt} image={post.image} />

            <div className="bg-brand-blue pt-32 grain">
                <div className="mx-auto max-w-3xl px-6 pb-12">
                    <nav className="flex items-center gap-2 text-xs text-white/60">
                        <Link to="/blog" className="hover:text-white">Blog</Link>
                        <ChevronRight className="h-3.5 w-3.5" />
                        <span className="text-white">{post.category}</span>
                    </nav>
                    <h1 className="mt-6 font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">{post.title}</h1>
                    <div className="mt-6 flex flex-wrap gap-5 text-sm text-white/70">
                        <span className="flex items-center gap-2"><User className="h-4 w-4 text-brand-red" />{post.author}</span>
                        <span className="flex items-center gap-2"><Calendar className="h-4 w-4 text-brand-red" />{fmt(post.date)}</span>
                    </div>
                </div>
            </div>

            <article className="py-16">
                <div className="mx-auto max-w-3xl px-6">
                    <Reveal>
                        <div className="overflow-hidden rounded-[28px] shadow-brand-lg">
                            <img src={post.image} alt={post.title} className="max-h-[440px] w-full object-cover" />
                        </div>
                    </Reveal>

                    <ArticleBody html={post.contentHtml} />

                    {/* Share */}
                    <div className="mt-10 flex items-center gap-3 border-t border-brand-blue/10 pt-8">
                        <span className="flex items-center gap-2 text-sm font-semibold text-brand-blue"><Share2 className="h-4 w-4" /> Share</span>
                        <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-surface text-brand-blue hover:bg-brand-blue hover:text-white"><Facebook className="h-4 w-4" /></a>
                        <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on LinkedIn" className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-surface text-brand-blue hover:bg-brand-blue hover:text-white"><Linkedin className="h-4 w-4" /></a>
                        <a href={waLink(`Check out this ProCare.SG article: ${post.title} ${shareUrl}`)} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-surface text-brand-blue hover:bg-brand-red hover:text-white"><WhatsAppIcon className="h-4 w-4" /></a>
                    </div>

                    {/* WhatsApp CTA */}
                    <div className="mt-10 rounded-[28px] bg-brand-surface p-8 text-center">
                        <p className="font-display text-xl font-semibold text-brand-blue">Need help with your property?</p>
                        <p className="mt-2 text-sm text-brand-ink/60">Chat with our team for a free, no-obligation quote.</p>
                        <div className="mt-5 flex justify-center">
                            <CTAButton href={waLink(WA_MESSAGES.general)} whatsapp testid="blogpost-whatsapp-cta">WhatsApp Us</CTAButton>
                        </div>
                    </div>
                </div>
            </article>

            {/* Related */}
            {related.length > 0 && (
                <section className="bg-brand-surface py-20">
                    <div className="mx-auto max-w-7xl px-6">
                        <SectionLabel>Recent posts</SectionLabel>
                        <h3 className="mt-4 font-display text-2xl font-semibold text-brand-blue">Keep reading</h3>
                        <div className="mt-8 grid gap-6 md:grid-cols-3">
                            {related.map((p) => (
                                <Link key={p.id} to={`/blog/${p.slug}`} className="group overflow-hidden rounded-3xl bg-white transition-all hover:-translate-y-1 hover:shadow-brand">
                                    <div className="aspect-[16/10] overflow-hidden">
                                        <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                                    </div>
                                    <div className="p-5">
                                        <span className="text-xs font-bold uppercase tracking-widest text-brand-red">{p.category}</span>
                                        <h4 className="mt-2 font-display text-base font-semibold leading-snug text-brand-blue">{p.title}</h4>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </>
    );
}
